import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Mail, Phone, Building2, Globe, Code2 } from 'lucide-react';

export const Login = () => {
  const { login } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [site, setSite] = useState(null);

  useEffect(() => {
    const savedEmail = localStorage.getItem('savedEmail');
    const savedRemember = localStorage.getItem('rememberMe') === 'true';
    if (savedEmail && savedRemember) { setEmail(savedEmail); setRememberMe(true); }
    axios.get(`${API}/site-settings`).then(r => setSite(r.data)).catch((err) => console.warn('[Login]', err));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (rememberMe) { localStorage.setItem('savedEmail', email); localStorage.setItem('rememberMe', 'true'); }
      else { localStorage.removeItem('savedEmail'); localStorage.removeItem('rememberMe'); }
      login(response.data.token, response.data.user);
      toast.success('Innlogget!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Innlogging feilet'); }
    finally { setLoading(false); }
  };

  const s = site || {};
  const bgImage = s.background_image_url || 'https://images.unsplash.com/photo-1763626343502-b0b1f26b43d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxiZWVyJTIwdGFwJTIwYmFyJTIwY291bnRlcnxlbnwwfHx8fDE3NjYwOTUzOTN8MA&ixlib=rb-4.1.0&q=85';

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" style={{ backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.7)), url('${bgImage}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-white mb-2" data-testid="login-title" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
              {s.site_title || 'TapService Pro'}
            </h1>
            <p className="text-slate-300" data-testid="login-subtitle">{s.site_subtitle || ''}</p>
          </div>
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle>{s.login_card_title || 'Logg inn'}</CardTitle>
              <CardDescription>{s.login_card_subtitle || 'Skriv inn dine påloggingsdetaljer'}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">E-post</Label>
                  <Input id="email" data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="din@epost.no" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Passord</Label>
                  <Input id="password" data-testid="password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="rememberMe" checked={rememberMe} onCheckedChange={(checked) => setRememberMe(checked)} />
                    <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer" style={{ color: '#475569' }}>Husk meg</Label>
                  </div>
                  <button type="button" data-testid="forgot-password-link" onClick={() => setForgotPasswordOpen(true)} className="text-sm text-brand-600 hover:text-brand-700 hover:underline">Glemt passord?</button>
                </div>
                <Button type="submit" data-testid="login-button" className="w-full bg-slate-900 hover:bg-slate-800 text-white" disabled={loading}>
                  {loading ? 'Logger inn...' : 'Logg inn'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {s.site_description && (
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-300" data-testid="login-description">{s.site_description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-4 text-center">
        <div className="max-w-lg mx-auto space-y-3">
          {/* Company info */}
          {(s.footer_company_name || s.footer_org_number || s.footer_email) && (
            <div className="bg-slate-900/80 backdrop-blur-sm rounded-lg p-4">
              {s.footer_company_name && <p className="text-white font-medium mb-2">{s.footer_company_name}</p>}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm text-slate-300">
                {s.footer_org_number && <span className="flex items-center gap-1"><Building2 className="h-4 w-4" />{s.footer_org_number}</span>}
                {s.footer_org_number && s.footer_email && <span className="hidden sm:inline text-slate-500">|</span>}
                {s.footer_email && <a href={`mailto:${s.footer_email}`} className="flex items-center gap-1 hover:text-white transition-colors"><Mail className="h-4 w-4" />{s.footer_email}</a>}
                {s.footer_phone && <><span className="hidden sm:inline text-slate-500">|</span><a href={`tel:${s.footer_phone}`} className="flex items-center gap-1 hover:text-white transition-colors"><Phone className="h-4 w-4" />{s.footer_phone}</a></>}
              </div>
            </div>
          )}

          {/* Developer info */}
          {(s.developer_name || s.developer_email || s.developer_phone || s.developer_website) && (
            <div className="bg-slate-800/60 backdrop-blur-sm rounded-lg px-4 py-3">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-xs text-slate-400">
                <span className="flex items-center gap-1" data-testid="login-developer"><Code2 className="h-3.5 w-3.5" />Utviklet av {s.developer_name || 'utvikler'}</span>
                {s.developer_email && <><span className="hidden sm:inline text-slate-600">|</span><a href={`mailto:${s.developer_email}`} className="hover:text-slate-200 transition-colors">{s.developer_email}</a></>}
                {s.developer_phone && <><span className="hidden sm:inline text-slate-600">|</span><a href={`tel:${s.developer_phone}`} className="hover:text-slate-200 transition-colors">{s.developer_phone}</a></>}
                {s.developer_website && <><span className="hidden sm:inline text-slate-600">|</span><a href={s.developer_website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-slate-200 transition-colors"><Globe className="h-3 w-3" />{s.developer_website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a></>}
              </div>
            </div>
          )}
        </div>
      </footer>

      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Glemt passord?</DialogTitle>
            <DialogDescription>Kontakt din administrator for å få tilbakestilt passordet ditt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600">Av sikkerhetsgrunner kan passord kun tilbakestilles av en administrator.</p>
            </div>
          </div>
          <Button onClick={() => setForgotPasswordOpen(false)} className="w-full bg-slate-900 hover:bg-slate-800">Lukk</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
